import { gqlForgotPassword } from './login.graphql';
import { environment } from '../../../../environments/environment';
import { PublicApiService } from '../../../core/services/public-api.service';
import { CoreStore } from '../../../core/store/core.store';
import { getLoginJWTToken } from '../store/login.selectors';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ErrorCode, getLoggedInUserProfile } from '@ppl/auth';
import { getAppAbsoluteURL } from '@ppl/core';
import { gqlSendVerificationLinkToEmail, SendVerificationLinkToEmailErrorWhiteList } from '@ppl/email-verification-shared';
import { of, Subject } from 'rxjs';
import { finalize, map, switchMap } from 'rxjs/operators';
import type { HttpErrorResponse } from '@angular/common/http';
import type { ApiError } from '@ppl/auth';
import type { SendVerificationLinkToEmailInput, SendVerificationLinkToEmailResult } from '@ppl/graphql-public-api';
import type { Space } from '@ppl/graphql-user-api';
import type { Observable } from 'rxjs';
import type { User } from '../store/login.reducer';

export interface Credentials {
  username: string;
  password?: string;
  remember?: boolean;
}

export interface UserData {
  first_name: string;
  last_name: string;
  terms_and_conditions: boolean;
  old_password?: string;
  password?: string;
}

export enum LoginError {
  UNKNOWN_ERROR = ErrorCode.ERROR_UNKNOWN,
  NO_ERROR = ErrorCode.NO_ERROR,
  ERROR_INVALID_MESSAGE = ErrorCode.ERROR_INVALID_MESSAGE,
  ERROR_INCORRECT_LOGIN = ErrorCode.ERROR_INCORRECT_LOGIN,
  ERROR_SESSION_EXPIRED = ErrorCode.ERROR_SESSION_EXPIRED,
  // NOTE(mike): Possible error when authentication to 3rd IDP
  // failed.
  ERROR_3RD_PARTY_AUTH_FAILED = ErrorCode.ERROR_3RD_PARTY_AUTH_FAILED,
  ERROR_UNKNOWN_USER = ErrorCode.ERROR_UNKNOWN_USER,
  ERROR_UNEXPECTED_USER = ErrorCode.ERROR_UNEXPECTED_USER,
  ERROR_ACCOUNT_DISABLED = ErrorCode.ERROR_ACCOUNT_DISABLED,
  // NOTE(mike): Only FE related error codes
  ERROR_TOO_MANY_FAILED_LOGIN_ATTEMPTS = 100000,
}

export enum ForgotPasswordError {
  UNKNOWN_ERROR = ErrorCode.ERROR_UNKNOWN,
  NO_ERROR = ErrorCode.NO_ERROR,
  ERROR_TOO_MANY_REQUESTS = ErrorCode.ERROR_TOO_MANY_REQUESTS
}

export enum PasswordResetError {
  UNKNOWN_ERROR = ErrorCode.ERROR_UNKNOWN,
  NO_ERROR = ErrorCode.NO_ERROR,
  ERROR_SESSION_EXPIRED = ErrorCode.ERROR_SESSION_EXPIRED,
  ERROR_INVALID_MESSAGE = ErrorCode.ERROR_INVALID_MESSAGE,
  ERROR_INCORRECT_LOGIN = ErrorCode.ERROR_INCORRECT_LOGIN
}

export interface PasswordResetAPIResponse {
  error: PasswordResetError;
  redirect_url?: string;
}

export type ResendVerificationEmailError = ErrorCode.ERROR_UNKNOWN
  | ErrorCode.NO_ERROR
  | ErrorCode.ERROR_EMAIL_NOT_VERIFIED
  | ErrorCode.ERROR_TOO_MANY_REQUESTS
  | ErrorCode.ERROR_SENDING_MAIL;

export interface ResendVerificationEmailResponse {
  error: ResendVerificationEmailError;
  redirect_url?: string;
}

export enum WelcomeError {
  ERROR_INVALID_DATA = -1,
  NO_ERROR = ErrorCode.NO_ERROR,
  UNKNOWN_ERROR = ErrorCode.ERROR_UNKNOWN,
  ERROR_SESSION_EXPIRED = ErrorCode.ERROR_SESSION_EXPIRED,
  ERROR_INVALID_MESSAGE = ErrorCode.ERROR_INVALID_MESSAGE,
  ERROR_INCORRECT_LOGIN = ErrorCode.ERROR_INCORRECT_LOGIN,
  ERROR_INCORRECT_PASSWORD = ErrorCode.ERROR_INCORRECT_PASSWORD,
  ERROR_TERMS_CONDITIONS_NOT_ACCEPTED = ErrorCode.ERROR_TERMS_CONDITIONS_NOT_ACCEPTED,
}

export interface WelcomeAPIResponse {
  error: ErrorCode;
  error_str?: string;
  redirect_url?: string;
}

type WelcomeAPIErrorResponse = Pick<WelcomeAPIResponse, 'error' | 'error_str'>;

export interface WelcomeErrorInfo {
  error: WelcomeError;
  errorStr?: string;
}

export function convertWelcomeAPIErrorResponseToWelcomeErrorInfo(response: WelcomeAPIErrorResponse): WelcomeErrorInfo {
  switch (response.error) {
    case ErrorCode.ERROR_ENTITY_VALIDATION: {
      return {
        error: WelcomeError.ERROR_INVALID_DATA,
        errorStr: response.error_str
      };
    }
    case ErrorCode.NO_ERROR:
    case ErrorCode.ERROR_UNKNOWN:
    case ErrorCode.ERROR_SESSION_EXPIRED:
    case ErrorCode.ERROR_INVALID_MESSAGE:
    case ErrorCode.ERROR_INCORRECT_LOGIN:
    case ErrorCode.ERROR_INCORRECT_PASSWORD:
    case ErrorCode.ERROR_TERMS_CONDITIONS_NOT_ACCEPTED:
      return {
        error: ({
          [ErrorCode.NO_ERROR]: WelcomeError.NO_ERROR,
          [ErrorCode.ERROR_UNKNOWN]: WelcomeError.UNKNOWN_ERROR,
          [ErrorCode.ERROR_SESSION_EXPIRED]: WelcomeError.ERROR_SESSION_EXPIRED,
          [ErrorCode.ERROR_INVALID_MESSAGE]: WelcomeError.ERROR_INVALID_MESSAGE,
          [ErrorCode.ERROR_INCORRECT_LOGIN]: WelcomeError.ERROR_INCORRECT_LOGIN,
          [ErrorCode.ERROR_INCORRECT_PASSWORD]: WelcomeError.ERROR_INCORRECT_PASSWORD,
          [ErrorCode.ERROR_TERMS_CONDITIONS_NOT_ACCEPTED]: WelcomeError.ERROR_TERMS_CONDITIONS_NOT_ACCEPTED,
        })[response.error]
      };
    default:
      return {
        error: WelcomeError.UNKNOWN_ERROR
      };
  }
}

export enum SelectedSpaceError {
  UNKNOWN_ERROR = ErrorCode.ERROR_UNKNOWN,
  NO_ERROR = ErrorCode.NO_ERROR,
  ERROR_SESSION_EXPIRED = ErrorCode.ERROR_SESSION_EXPIRED
}

export interface SelectedSpaceApiResponse {
  error: SelectedSpaceError;
  redirect_url: '';
  state: '';
}

export enum LoginState {
  NEEDS_SETUP = 'needs_setup',
  SUCCESS = 'success',
  EXTERNAL = 'external',
  SELECT_TEAMSPACE = 'select_teamspace',
  NEEDS_TWO_FA = 'needs_two_fa'
}

export interface LoginAPIResponse {
  error: LoginError;
  state?: LoginState;
  redirect_url?: string;
  name?: string;
  picture?: string;
  user?: User;
}

@Injectable()
export class LoginService {

  constructor(
    private store: CoreStore,
    private http: HttpClient,
    private publicApiService: PublicApiService,
    private router: Router
  ) { }

  getJWTToken() {
    return this.store.get(getLoginJWTToken);
  }

  login(credentials: Credentials) {
    const subject = new Subject<LoginAPIResponse>();

    const jwt = this.getJWTToken();

    this.store.dispatch('Login_Validating', true);
    this.store.dispatch('Login_SetError', { error: LoginError.NO_ERROR });
    this.http.post(
      '/rest/login',
      {
        ...credentials,
        jwt
      }
    ).subscribe(
      (response: LoginAPIResponse) => {
        if (response.error) {
          this.store.dispatch('Login_SetError', { error: response.error });
          subject.error(response.error);
        } else {
          switch (response.state) {
            case LoginState.NEEDS_TWO_FA:
            case LoginState.SUCCESS: {
              window.location.href = getAppAbsoluteURL(response.redirect_url);
            } break;
            case LoginState.EXTERNAL: {
              window.location.href = getAppAbsoluteURL(response.redirect_url);
            } break;
            case LoginState.NEEDS_SETUP: {
              this.store.dispatch('Login_SetUser', response.user);
              this.router.navigate(['welcome', jwt]);
            } break;
            default: {
              this.store.dispatch('Login_SetLoginUser', {
                name: response.name
              });
              subject.next(response);
              subject.complete();
            } break;
          }
        }
      },
      (error: HttpErrorResponse) => {
        if (error.status === 429) {
          this.store.dispatch(
            'Login_SetError',
            { error: LoginError.ERROR_TOO_MANY_FAILED_LOGIN_ATTEMPTS }
          );
          subject.error(LoginError.ERROR_TOO_MANY_FAILED_LOGIN_ATTEMPTS);
        } else {
          this.store.dispatch('Login_SetError', { error: LoginError.UNKNOWN_ERROR });
          subject.error(LoginError.UNKNOWN_ERROR);
        }
      }
    );
    return subject.pipe(
      finalize(() => {
        this.store.dispatch('Login_Validating', false);
      })
    );
  }

  forgotPassword(email: string) {
    const forgotPasswordSubject = new Subject();

    this.publicApiService.mutate({
      mutation: gqlForgotPassword,
      variables: {
        input: {
          email,
        }
      }
    }).subscribe(
      ({ data }) => {
        forgotPasswordSubject.next();
        forgotPasswordSubject.complete();
      },
      (error) => {
        const apiErrors: ApiError[] = error.graphQLErrors
          ? error.graphQLErrors
            .map(gqlError => gqlError.api_error)
            .filter(apiError => !!apiError)
          : [];
        if (apiErrors && apiErrors.length) {
          const errorCode = apiErrors[0].code;
          forgotPasswordSubject.error(errorCode || ForgotPasswordError.UNKNOWN_ERROR);
        } else {
          forgotPasswordSubject.error(ForgotPasswordError.UNKNOWN_ERROR);
        }
      }
    );

    return forgotPasswordSubject.asObservable();
  }

  passwordReset(hash: string, password: string) {
    const subject = new Subject();

    this.http.post(
      '/rest/password-reset',
      {
        pw_reset_hash: hash,
        password
      }
    ).subscribe(
      (response: PasswordResetAPIResponse) => {
        switch (response.error) {
          case PasswordResetError.NO_ERROR: {
            window.location.href = getAppAbsoluteURL(response.redirect_url);
          } break;
          default: {
            subject.error(response.error);
          } break;
        }
      },
      (error) => {
        subject.error(PasswordResetError.UNKNOWN_ERROR);
      }
    );
    return subject.asObservable();
  }

  updateUserData(userData: UserData) {
    const subject = new Subject<void>();
    const auth = { jwt: this.getJWTToken() };

    this.http.post(
      '/rest/1st-time', {
      ...auth,
      ...userData
    }
    ).subscribe(
      (response: WelcomeAPIResponse) => {
        switch (response.error) {
          case ErrorCode.NO_ERROR: {
            window.location.href = getAppAbsoluteURL(response.redirect_url);
          } break;
          default: {
            subject.error(convertWelcomeAPIErrorResponseToWelcomeErrorInfo(response));
          } break;
        }
      },
      (error) => {
        subject.error(<WelcomeErrorInfo>{
          error: WelcomeError.UNKNOWN_ERROR
        });
      }
    );

    return subject.asObservable();
  }

  notifyUserChanged() {
    return this.http.get('/rest/user_changed');
  }

  getRedirectUrlAfterSpaceSelect(space: Space, changeUsersDefaultIdp = false): Observable<SelectedSpaceApiResponse> {
    const jwt = this.getJWTToken();
    return this.http.post(
      '/rest/space-selected',
      {
        username: this.store.get(getLoggedInUserProfile).email,
        team_space_id: space.id,
        store_idp: changeUsersDefaultIdp,
        idp_id: space.idp && space.idp.enabled ? space.idp.id : 0, // 0 instead of null because API can not handle null correctly (#apifail)
        jwt,
        success_url: jwt ? null : `${environment.crmAppUrl}/space/${space.id}`
      }
    ).pipe(
      switchMap((result: SelectedSpaceApiResponse) => {
        if (result.error === SelectedSpaceError.NO_ERROR) {
          return of(result);
        }
        throw new Error(result.error.toString());
      })) as Observable<SelectedSpaceApiResponse>;
  }

  resendVerificationEmail(): Observable<ResendVerificationEmailResponse> {
    const jwt = this.getJWTToken();

    return this.publicApiService.mutate<
      { sendVerificationLinkToEmail: SendVerificationLinkToEmailResult },
      { input: SendVerificationLinkToEmailInput }
    >({
      mutation: gqlSendVerificationLinkToEmail,
      variables: {
        input: {
          token: jwt,
        }
      },
      context: {
        errorWhiteList: [
          ...SendVerificationLinkToEmailErrorWhiteList
        ]
      }
    }).pipe(
      map(({ data }) => {
        const result = data.sendVerificationLinkToEmail;
        return {
          error: result.sent
            ? ErrorCode.NO_ERROR
            : ErrorCode.ERROR_SENDING_MAIL
        };
      })
    );
  }
}
